/**
 * JSON-Render Provider Components
 *
 * Wraps the application with necessary providers for json-render
 * including data context, action handling, and visibility rules.
 */

import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { DataProvider, ActionProvider, VisibilityProvider } from '@json-render/react';

import { createActionDispatcher } from '../../lib/json-render/actions';

/**
 * Commerce context for sharing state between providers
 */
const CommerceContext = createContext({});

/**
 * Layout context for rendering mode (inline vs panel)
 */
const LayoutContext = createContext({ inline: false });

/**
 * Hook to access layout context
 */
export function useLayoutContext() {
  return useContext(LayoutContext);
}

/**
 * Hook to access commerce context
 */
export function useCommerceContext() {
  return useContext(CommerceContext);
}

/**
 * Main commerce provider that wraps json-render providers
 */
export function CommerceProvider({
  children,
  // Navigation
  navigate,
  // Cart state and actions
  cart = { items: [], total: 0 },
  addToCart,
  // User state
  user = { isAuthenticated: false, preferences: {} },
  // Session
  sessionId,
  // Search
  sendMessage,
  searchResults = [],
  // Product interaction
  onProductClick,
  // Wishlist
  saveToWishlist,
  // Price alerts
  createPriceAlert,
}) {
  // Build initial data for DataProvider
  const initialData = useMemo(() => ({
    cart: {
      items: cart.items || [],
      total: cart.total || 0,
      itemCount: cart.items?.length || 0,
    },
    user: {
      isAuthenticated: user.isAuthenticated || false,
      preferences: user.preferences || {},
      loyaltyTier: user.loyaltyTier || null,
    },
    search: {
      results: searchResults,
      query: '',
      filters: {},
    },
  }), [cart, user, searchResults]);

  // Create action dispatcher with all context
  const actionDispatcher = useMemo(() => createActionDispatcher({
    navigate,
    addToCart,
    sessionId,
    sendMessage,
    saveToWishlist,
    createPriceAlert,
    onProductClick,
  }), [navigate, addToCart, sessionId, sendMessage, saveToWishlist, createPriceAlert, onProductClick]);

  // Action handler wrapper for ActionProvider
  const handleAction = useCallback(async (action) => {
    console.log('[CommerceProvider] Action received:', action);
    return actionDispatcher(action);
  }, [actionDispatcher]);

  // Commerce context value
  const commerceValue = useMemo(() => ({
    sessionId,
    cart,
    user,
    searchResults,
    dispatch: actionDispatcher,
  }), [sessionId, cart, user, searchResults, actionDispatcher]);

  return (
    <CommerceContext.Provider value={commerceValue}>
      <DataProvider initialData={initialData}>
        <VisibilityProvider>
          <ActionProvider onAction={handleAction}>
            {children}
          </ActionProvider>
        </VisibilityProvider>
      </DataProvider>
    </CommerceContext.Provider>
  );
}

/**
 * Context for passing callbacks to deeply nested components
 */
const ChatCallbackContext = createContext({ sendMessage: null });

export function useChatCallbacks() {
  return useContext(ChatCallbackContext);
}

/**
 * Simplified provider for standalone use
 */
export function SimpleCommerceProvider({ children, sessionId: _sessionId, onAction, inline = false }) {
  const initialData = useMemo(() => ({
    cart: { items: [], total: 0, itemCount: 0 },
    user: { isAuthenticated: false, preferences: {} },
    search: { results: [], query: '', filters: {} },
  }), []);

  const handleAction = useCallback(async (action) => {
    console.log('[SimpleCommerceProvider] Action received:', action);
    if (onAction) {
      const result = await onAction(action);
      console.log('[SimpleCommerceProvider] Action result:', result);
      return result;
    }
    console.log('[SimpleCommerceProvider] No onAction handler');
    return { success: true };
  }, [onAction]);

  // Also pass onAction through a dedicated context
  const chatCallbackValue = useMemo(() => ({
    sendMessage: (query) => {
      console.log('[ChatCallback] sendMessage called:', query);
      onAction?.({ name: 'refine_search', params: { query } });
    }
  }), [onAction]);

  const layoutValue = useMemo(() => ({ inline }), [inline]);

  return (
    <ChatCallbackContext.Provider value={chatCallbackValue}>
      <LayoutContext.Provider value={layoutValue}>
        <DataProvider initialData={initialData}>
          <VisibilityProvider>
            <ActionProvider onAction={handleAction}>
              {children}
            </ActionProvider>
          </VisibilityProvider>
        </DataProvider>
      </LayoutContext.Provider>
    </ChatCallbackContext.Provider>
  );
}

export default CommerceProvider;
